import React, { useMemo } from "react";
import { Form, message } from "antd";
import StyledModal from "../../../components/CustomModal";
import { FooterButtons, StyledButton } from "../../../components/styles";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import ItemDetailsForm from "./steps/ItemDetailsForm";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import { updateFoundItemByAdmin } from "../../../redux/features/foundReport/foundReportAsyncThunk";
import { handleAddMergeDateTime, handleMergeDateTime } from "../../../utils/helpers/dateFunctions";
import { setQuestionData } from "../../../redux/features/question/questionSlice";
import { transformFoundItemResponse } from "../utils/const";
import { updateTicketLocally } from "../../../redux/features/board/columnsSlice";
import { ReportType } from "../../../redux/features/board/columnTypes";
import { convertToApiFormat } from "../../../utils/helpers/unitConversion";
import { validateImperialValue, validateMetricValue } from "../../ReportDetails/utils/formula";
import { FoundItemType, FoundItemTypeOptions, UnitType, UnitTypeOptions } from "../../../types/FoundItemTypes";

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

interface EditReportDetailsModalProps {
    isModalOpen: boolean;
    handleOk: () => void;
    handleCancel: () => void;
}

const EditReportDetailsModal: React.FC<EditReportDetailsModalProps> = ({
    isModalOpen,
    handleOk,
    handleCancel
}) => {
    const [form] = Form.useForm();
    const { loading, data } = useAppSelector((state) => state.foundReport);
    const { categoryState, subCategoryState } = useAppSelector((state) => state.generaleWebData);
    const { data: questions } = useAppSelector((state) => state.question);
    const { shipState, locationState } = useAppSelector((state) => state.generaleWebData);
    const { status: shipStatus } = useMemo(() => shipState, [shipState]);
    const { status: locationStatus } = useMemo(() => locationState, [locationState]);
    const { status: categoryStatus } = useMemo(() => categoryState, [categoryState]);
    const { status: subCategoryStatus } = useMemo(() => subCategoryState, [subCategoryState]);

    const isLoading = useMemo(() => loading || shipStatus === "loading" || locationStatus === "loading" || categoryStatus === "loading" || subCategoryStatus === "loading", [loading, locationStatus, shipStatus, categoryStatus, subCategoryStatus])
    const foundItemType: FoundItemType = Form.useWatch("foundType", form) || 1;
    const unit: UnitType = Form.useWatch("measurementType", form) || 1;
    const hasLuggageItemType = useMemo(() => FoundItemTypeOptions.FoundLuggage === foundItemType, [foundItemType]);

    const dispatch = useAppDispatch();

    const handleFinish = async () => {
        try {
            const values = await form.validateFields();

            const locationAnswers = Object.entries({ ...values })
                .filter(([key]) => key.startsWith('locationQuestion_'))
                .map(([key, value]) => ({
                    question: key.replace('locationQuestion_', ''),
                    answer: value
                }));

            const answers = questions?.length > 0
                ? questions.map((question) => {
                    // Get the answer for the main question
                    const answerUUID = values[`question_${question.id}`]; // Store the UUID of the answer
                    delete values[`question_${question.id}`];  // Remove the answer from values

                    // Find the corresponding name of the answer for the main question
                    const selectedOption = question.options?.find(option => option.id === answerUUID);
                    const answerName = selectedOption ? selectedOption.name ?? "" : answerUUID; // If found, use name, else keep UUID
                    const parentID = questions?.length > 0
                        ? questions.find((question) =>
                            question.options?.some((option) => option.id === selectedOption?.id)
                        )?.id
                        : null;

                    // Start by storing the main question's answer with its name
                    const combinedAnswers = [{ question: question.id, answer: answerName }];

                    // If the current question is the "Brand" question (ID: 643f1b0e-fd7a-444f-b00e-ff97fa816497), remove subQuestions
                    if (question.id === parentID) {
                        // Remove subQuestion answers for this question
                        question.options?.forEach((option) => {
                            if (option?.name === selectedOption?.name) {
                                // Keep the subQuestion answers for "Samsung"
                                option?.subQuestion?.forEach((subQ: any) => {
                                    const subAnswerUUID = values[`subQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                                    if (subAnswerUUID !== undefined) {
                                        // Find the corresponding subQuestion name
                                        const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                                        const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                                        // Add subQuestion answer to the list with its name
                                        combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                                    }
                                    // Remove subQuestion answer from values
                                    delete values[`subQuestion_${subQ.id}`];
                                });
                            } else {
                                option?.subQuestion?.forEach((subQ: any) => {
                                    delete values[`subQuestion_${subQ.id}`]; // Remove subQuestion answer from values
                                });
                            }
                        });
                    } else {
                        // Otherwise, check if there are subQuestions for this question and handle them
                        question.options?.forEach((option) => {
                            option?.subQuestion?.forEach((subQ: any) => {
                                const subAnswerUUID = values[`subQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                                if (subAnswerUUID !== undefined) {
                                    // Find the corresponding subQuestion name
                                    const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                                    const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                                    // Add subQuestion answer to the list with its name
                                    combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                                }
                                // Remove subQuestion answer from values
                                delete values[`subQuestion_${subQ.id}`];
                            });
                        });
                    }

                    // Return the combined list of main question + subQuestion
                    return combinedAnswers?.filter(answer => answer.answer !== undefined);
                })
                : [];

            // Flatten the answers into one list (because we might have multiple answers per question)
            const flattenedAnswers = answers?.flat()?.filter(answer => answer.answer !== undefined);

            const cleanedFormData = { ...values };
            Object.keys(cleanedFormData).forEach(key => {
                if (key.startsWith('question_') || key.startsWith('subQuestion_')) {
                    delete cleanedFormData[key];
                }
            });

            // const files = values?.documents || [];
            let files = [];
            if (Array.isArray(values?.documents)) {
                const hasBlob = values.documents.some((doc: any) => doc instanceof Blob);
                if (hasBlob) {
                    files = values?.documents;
                }
            }
            delete cleanedFormData.documents;
            const emptyArray = JSON.stringify([]);


            if (hasLuggageItemType) {
                const normalizedValues = {
                    length: unit === UnitTypeOptions.Imperial ? validateImperialValue("length", values.length) : validateMetricValue("length", values.length),
                    width: unit === UnitTypeOptions.Imperial ? validateImperialValue("width", values.width) : validateMetricValue("width", values.width),
                    height: unit === UnitTypeOptions.Imperial ? validateImperialValue("height", values.height) : validateMetricValue("height", values.height),
                    weight: unit === UnitTypeOptions.Imperial ? validateImperialValue("weight", values.weight) : validateMetricValue("weight", values.weight),
                };

                if (unit === UnitTypeOptions.Imperial) {
                    const totalDimensions = (normalizedValues.length.main || 0) + (normalizedValues.width.main || 0) + (normalizedValues.height.main || 0);

                    if (totalDimensions > 150) {
                        message.error("Total dimensions cannot exceed 150 feet");
                        return;
                    }

                    if (normalizedValues.weight.main || 0 > 15) {
                        message.error("Weight cannot exceed 15 lb");
                        return;
                    }
                } else {
                    const totalDimensions = (normalizedValues.length.main || 0) + (normalizedValues.width.main || 0) + (normalizedValues.height.main || 0);

                    if (totalDimensions > 45) {
                        message.error("Total dimensions cannot exceed 45 meters");
                        return;
                    }

                    if ((normalizedValues.weight.main || 0) > 6.8) {
                        message.error("Weight cannot exceed 6.8 kg");
                        return;
                    }
                }
            }

            const formattedValues = {
                ...cleanedFormData,
                answers: flattenedAnswers?.length > 0 ? flattenedAnswers : emptyArray,
                files,
                locationAnswers: locationAnswers?.length > 0 ? locationAnswers : emptyArray,
                foundDate: dayjs(values?.foundDate).isValid() ? handleMergeDateTime(values?.foundDate)?.toISOString() : "",
                offloadDate: values?.offloadDate && dayjs(values?.offloadDate).isValid() ? handleAddMergeDateTime(values?.offloadDate)?.toISOString() : "null",
                ...(hasLuggageItemType ? { ...convertToApiFormat(cleanedFormData, cleanedFormData.measurementType) } : {}),
            };

            const response = await dispatch(updateFoundItemByAdmin({ id: data?.id || "", formData: formattedValues }));
            if (response?.payload) {
                const transformedResponse = await transformFoundItemResponse(response.payload);
                await dispatch(updateTicketLocally({ ticket: { ...transformedResponse, isFoundItem: true, foundType: hasLuggageItemType ? FoundItemTypeOptions.FoundLuggage : FoundItemTypeOptions.FoundItem, isDraft: false }, type: ReportType.Found }));
            }
            form.resetFields();
            handleOk();
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const onHandleCancel = async () => {
        form.resetFields();
        await dispatch(setQuestionData([]));
        handleCancel();
    };

    return (
        <StyledModal
            draggable={true}
            title={`Update Found ${hasLuggageItemType ? "Luggage" : "Item"}`}
            open={isModalOpen}
            closeIcon={null}
            width={1024}
            footer={
                <FooterButtons>
                    <StyledButton onClick={onHandleCancel} disabled={isLoading}>
                        Cancel
                    </StyledButton>
                    <StyledButton type="primary" onClick={handleFinish} disabled={isLoading} loading={isLoading}>
                        Update
                    </StyledButton>
                </FooterButtons>
            }
            centered
            onCancel={onHandleCancel}
        >
            <ItemDetailsForm form={form} isEdit={true} />
        </StyledModal>
    );
};

export default EditReportDetailsModal; 